import PropTypes from "prop-types";
import {
  equals,
  find,
  gt,
  head,
  identity,
  partial,
  pluck,
  propEq,
  without,
} from "ramda";
import React from "react";
import { CSSTransition, TransitionGroup } from "react-transition-group";

import { CreditCardFormContainer } from "./payment/CreditCardFormContainer";
import CvvValidation from "components/online_ordering/checkout/cvv_validation";
import NewCreditCardForm from "components/online_ordering/checkout/new_credit_card_form";
import Picker from "components/picker";
import PickerOption from "components/picker_option";
import Toggle from "components/toggle";

import { withTracking } from "helpers/analytics";
import { getFullfillmentTypeFromSelectedTransport } from "helpers/online_ordering/fulfillment_types";

function scopePaymentOptionToAvailable(paymentOptions, selectedPaymentOption) {
  if (gt(paymentOptions.length, 1)) {
    return selectedPaymentOption;
  }

  return head(paymentOptions);
}

function Payment({
  checkout,
  consumer,
  flags,
  onBlurValidateCCField,
  onCvvUpdate,
  onNewCreditCardUpdate,
  onPaymentTypeUpdate,
  onSelectFromCardDropdown,
  orderingOptions,
  showForm,
  storeInfo,
  orderResponse,
}) {
  const {
    payment: {
      isAddingNewCard,
      paymentType: selectedPaymentOption,
      newCreditCard,
      selectedCard,
    },
  } = checkout;

  const { contactInfo } = consumer;
  const creditCardPayment = equals(selectedPaymentOption, "credit");
  const selectedFulfillmentType = getFullfillmentTypeFromSelectedTransport(
    orderingOptions.selectedTransport,
    storeInfo.fulfillmentTypes
  );
  const paymentOptions = without(
    "gift_card",
    selectedFulfillmentType.paymentMethods
  );

  let form;
  if (
    (isAddingNewCard && creditCardPayment) ||
    (equals(consumer.cards.length, 0) && creditCardPayment)
  ) {
    form = (
      <CreditCardFormContainer orderResponse={orderResponse}>
        <NewCreditCardForm
          contactInfo={contactInfo}
          flags={flags}
          newCreditCard={newCreditCard}
          onBlurValidateField={onBlurValidateCCField}
          onNewCreditCardUpdate={onNewCreditCardUpdate}
          orderingOptions={orderingOptions}
        />
      </CreditCardFormContainer>
    );
  }

  const card = find(propEq("dropdownFormat", selectedCard), consumer.cards);

  if (!isAddingNewCard && creditCardPayment && !card.validated) {
    form = (
      <CvvValidation
        card={card}
        onCvvUpdate={onCvvUpdate}
        onBlurValidateField={onBlurValidateCCField}
      />
    );
  }

  let picker;
  if (creditCardPayment && gt(consumer.cards.length, 0)) {
    const formattedOptions = [
      ...pluck("dropdownFormat", consumer.cards),
      "+ Add a new card",
    ];

    picker = (
      <div className="form">
        <div className="row">
          <label>Credit card</label>
          <Picker
            className="card-picker"
            currentOption={selectedCard}
            onOptionClick={onSelectFromCardDropdown}
            options={formattedOptions}
            OptionComponent={PickerOption}
            formatter={identity}
          />
        </div>
      </div>
    );
  }

  let paymentForm;
  if (showForm) {
    paymentForm = (
      <CSSTransition classNames="expanding" timeout={{ enter: 300, exit: 300 }}>
        <div className="payment">
          <Toggle
            options={paymentOptions}
            selectedOption={scopePaymentOptionToAvailable(
              paymentOptions,
              selectedPaymentOption
            )}
            handleChange={withTracking(
              partial(onPaymentTypeUpdate, [consumer.cards]),
              `Select ${selectedPaymentOption
                .charAt(0)
                .toUpperCase()}${selectedPaymentOption.slice(1)}`
            )}
          />
          {picker}
          {form}
        </div>
      </CSSTransition>
    );
  }

  return <TransitionGroup>{paymentForm}</TransitionGroup>;
}

Payment.propTypes = {
  checkout: PropTypes.object.isRequired,
  consumer: PropTypes.object.isRequired,
  flags: PropTypes.object,
  onBlurValidateCCField: PropTypes.func.isRequired,
  onCvvUpdate: PropTypes.func.isRequired,
  onNewCreditCardUpdate: PropTypes.func.isRequired,
  onPaymentTypeUpdate: PropTypes.func.isRequired,
  onSelectFromCardDropdown: PropTypes.func.isRequired,
  orderingOptions: PropTypes.object.isRequired,
  showForm: PropTypes.bool.isRequired,
  storeInfo: PropTypes.object.isRequired,
  orderResponse: PropTypes.object,
};

export default Payment;
